/**
 * Theme folder contains all angular pages that are used after loggin in.
 * Any pages before login - is in auth
 * This also contains any flaoting pages which are not part of any module
 */

import { ScrollingModule } from '@angular/cdk/scrolling';
import { DOCUMENT, NgClass, NgStyle, ViewportScroller } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    type OnInit,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AvatarModule } from '@openng/optimus-ui/avatar';
import { Button } from '@openng/optimus-ui/button';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { DrawerModule } from '@openng/optimus-ui/drawer';
import { SplitterModule } from '@openng/optimus-ui/splitter';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { DxTemplateDirective } from 'devextreme-angular/core';
import { DxDataGridModule } from 'devextreme-angular/ui/data-grid';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { productFruits } from 'product-fruits';
import { filter, lastValueFrom, pairwise } from 'rxjs';

import { AuthenticationService } from '@app/auth/services/authentication.service';
import { LearningHubComponent } from '@app/modules/learning-hub/components/learning-hub/learning-hub.component';
import { LearningHubService } from '@app/modules/learning-hub/services/learning-hub.service';
import { AIChatComponent } from '@app/shared/components/ai-chat/ai-chat.component';
import { AiChatHandleComponent } from '@app/shared/components/ai-chat-handle/ai-chat-handle.component';
import {
    NotificationComponent,
    TEMPLATE_FILTER_TYPE,
} from '@app/shared/components/notification/notification.component';
import {
    EXECUTION_ICON,
    NOTIFICATION_TYPES,
    OBJECT_DISPLAY_NAMES,
} from '@app/shared/constants/shared.constant';
import type { NotificationAdapter } from '@app/shared/models/notification.model';
import { DateFromNowPipe } from '@app/shared/pipes/date-from-now.pipe';
import { NameBadgePipe } from '@app/shared/pipes/name-badge.pipe';
import { ObjectIconPipe } from '@app/shared/pipes/object-color.pipe';
import { UniqueRgbColorPipe } from '@app/shared/pipes/unique-rgb-color.pipe';
import { AiChatDockService } from '@app/shared/services/ai-chat-dock.service';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { NotificationService } from '@app/shared/services/notification.service';
import { StorageService } from '@app/shared/services/storage.service';
import { Helpers } from '@app/shared/utils/helpers';
import { RouterHelper } from '@app/shared/utils/router-helper';
import { FooterComponent } from '@app/theme/layouts/footer/footer.component';
import { HeaderNavComponent } from '@app/theme/layouts/header-nav/header-nav.component';
import { SidebarNavComponent } from '@app/theme/layouts/sidebar-nav/sidebar-nav.component';

@Component({
    selector: 'cor-theme',
    templateUrl: './theme.component.html',
    styleUrls: ['./theme.component.scss'],
    imports: [
        RouterModule,
        DxDataGridModule,
        FooterComponent,
        HeaderNavComponent,
        DateFromNowPipe,
        DialogModule,
        DrawerModule,
        LearningHubComponent,
        NotificationComponent,
        SidebarNavComponent,
        DxTemplateDirective,
        TooltipModule,
        UniqueRgbColorPipe,
        NameBadgePipe,
        ObjectIconPipe,
        NgClass,
        NgStyle,
        AvatarModule,
        SplitterModule,
        ScrollingModule,
        AIChatComponent,
        AiChatHandleComponent,
        Button,
    ],
})
export class ThemeComponent implements OnInit {
    readonly navParam = injectQueryParams<'open' | 'closed'>('nav');

    protected NOTIFICATION_TYPES = NOTIFICATION_TYPES;
    protected TEMPLATE_FILTER_TYPE = TEMPLATE_FILTER_TYPE;
    protected EXECUTION_ICON = EXECUTION_ICON as Record<string, string>; // Since in datagrid we don't have typing so  use string type
    protected readonly OBJECT_DISPLAY_NAMES = OBJECT_DISPLAY_NAMES;

    protected readonly pageSize = computed(
        () => this.appConfigService.appConfig().paginationItemsPerPage,
    );
    protected readonly productFruitsId = computed(
        () => this.appConfigService.appConfig().productFruits,
    );

    private readonly currentUser = inject(AuthenticationService).currentUser;
    private readonly appConfigService = inject(AppConfigService);
    private readonly notificationService = inject(NotificationService);
    private readonly router = inject(Router);
    private readonly viewportScroller = inject(ViewportScroller);
    private readonly destroyRef = inject(DestroyRef);
    private readonly document = inject(DOCUMENT);
    private readonly storageService = inject(StorageService);
    protected readonly learningHubService = inject(LearningHubService);
    protected readonly aiChatDockService = inject(AiChatDockService);
    protected readonly showSidebar = signal(
        new URL(this.router.url, window.origin).pathname !== '/home',
    );
    protected readonly chatPanelSizes = signal<[number, number]>([72, 28]);

    protected readonly notifications = {
        unreadCount: signal(''),
        isUnreadListOpen: signal(false),
        isDialogOpen: signal(false),
        datasource: undefined as DataSource | undefined,
        colors: {
            [NOTIFICATION_TYPES.FAILURE]: {
                iconClass: 'pi pi-times-circle',
                colorClass: 'tw:text-red-500',
                backgroundClass: 'tw:border-red-200 tw:bg-red-50',
            },
            [NOTIFICATION_TYPES.INFO]: {
                iconClass: 'pi pi-check-circle ',
                colorClass: 'tw:text-blue-500',
                backgroundClass: 'tw:border-blue-200 tw:bg-blue-50',
            },
            [NOTIFICATION_TYPES.SUCCESS]: {
                iconClass: 'pi pi-check-circle',
                colorClass: 'tw:text-green-500',
                backgroundClass: 'tw:border-green-200 tw:bg-green-50',
            },
        },
    };

    protected onSplitterResizeEnd(event: { sizes: (number | string)[] }): void {
        this.chatPanelSizes.set([Number(event.sizes[0]), Number(event.sizes[1])]);
    }

    constructor() {
        this.appConfigService.appConfig$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(_ => {
            this.setupNotificationStore();
        });

        const setPFCardHeader = (tourId: number) => {
            const tour = (
                window.productFruits?.api.tours.getTours() as { id: number; name: string }[]
            )?.find(x => x.id === tourId);
            const headerEl = this.document
                .querySelector('.productfruits--container')
                ?.shadowRoot?.querySelector('.productfruits--card-header');
            if (tour && headerEl) headerEl.textContent = tour.name;
        };
        const setAllPFCardHeaders = () => {
            // Find all PF cards on the page, and set the headers with the tour name
            this.document.querySelectorAll('.productfruits--container').forEach(containerEl => {
                const tourId = Number.parseInt(
                    containerEl?.shadowRoot
                        ?.querySelector('.productfruits--card')
                        ?.getAttribute('data-pf-card-tour'),
                    10,
                );
                if (tourId) {
                    setPFCardHeader(tourId);
                }
            });
        };

        // Initialize productFruits for application tours
        window.productFruitsReady = () => {
            console.log('Help Center is ready!');
            this.learningHubService.productFruitsIsReady.set(true);

            const callback: any = (tourId: number) => {
                setPFCardHeader(tourId);
            };

            // As the user advances, update the tour name in the header
            window.productFruits.api.tours.listen('tour-advanced', callback);

            // On first load, check if any tour is running and update the tour name in the header
            setAllPFCardHeaders();
        };
        effect(() => {
            if (this.productFruitsId() && this.currentUser()?.name) {
                console.log('Configured Help Center with user:', this.currentUser().name);
                productFruits.init(this.productFruitsId(), 'en', {
                    username: this.currentUser().name,
                    email: this.currentUser().email,
                });
            }
        });

        // Set the sidebar collapsed state based on the query param - this localStorage item is read
        // by side-nav when it loads. Used by tours.
        toObservable(this.navParam)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(nav => {
                if (nav) this.storageService.setLocalStorage(`sidebar-collapsed-state`, nav);
            });
    }

    ngOnInit() {
        this.notificationService.unreadNotificationCount$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(count => {
                // We dont need to reload the notificationDatasource when notification panel is hidden.
                // Reload it when notification panel is open.
                if (this.notifications.isUnreadListOpen()) {
                    this.notifications.datasource?.reload();
                }
                this.notifications.unreadCount.set(count);
            });

        this.router.events
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(e => e instanceof NavigationEnd),
            )
            .subscribe(e =>
                this.showSidebar.set(
                    new URL(e.urlAfterRedirects, window.origin).pathname !== '/home',
                ),
            );
        this.router.events
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(e => e instanceof NavigationEnd),
                pairwise(), // emit previous and current NavigationEnd
            )
            .subscribe(([prev, curr]: [NavigationEnd, NavigationEnd]) => {
                // only scroll if the route actually changed
                if (prev.urlAfterRedirects !== curr.urlAfterRedirects) {
                    this.viewportScroller.scrollToPosition([0, 0]);
                }
            });
    }

    private setupNotificationStore() {
        this.notifications.datasource = new DataSource({
            store: new CustomStore({
                key: 'id',
                load: loadOptions => {
                    // FIXME: Need to improve this load() handling. Currently we only use:
                    //        - pagination and totalCount for the notification table
                    //        - The filter for unread DataSource
                    return lastValueFrom(
                        this.notificationService.getDataList({
                            skip: loadOptions.skip,
                            take: loadOptions.take,
                        }),
                    ).then(response => {
                        return { data: response.result, totalCount: response.pageInfo.total };
                    });
                },
            }),
        });
    }

    protected toggleNotificationSidebar() {
        this.notifications.isUnreadListOpen.update(x => !x);
        if (this.notifications.isUnreadListOpen()) {
            this.notifications.datasource?.reload();
        }
    }

    protected openNotificationDialog(): void {
        this.notifications.isUnreadListOpen.set(false);
        this.notifications.isDialogOpen.set(true);
    }

    protected markAsReadAndRedirect(notification: NotificationAdapter): void {
        if (!notification.isViewed) {
            this.notificationService
                .markNotificationAsRead(notification)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(response => {
                    if (response.result?.isViewed) {
                        notification.isViewed = true;
                        this.notificationService.refreshUnreadNotificationCount();
                    }
                });
        }
        this.redirectToNotification(notification);
    }

    protected onClearAllNotifications(): void {
        this.notificationService
            .onClearAllNotifications()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(_ => {
                this.notificationService.refreshUnreadNotificationCount();
            });
    }

    private redirectToNotification(notification: NotificationAdapter): void {
        const notificationRedirectUrl =
            notification.messageData.uiPath ??
            RouterHelper.buildRoute(
                Helpers.convertNotificationDataToUIRouteSchema(notification.messageData),
            );

        if (notificationRedirectUrl) {
            this.router.navigateByUrl(notificationRedirectUrl).then(() => {
                this.notifications.isUnreadListOpen.set(false);
            });
        }
    }
}
